import { useRef, useState } from 'react';
import { api } from '../lib/api.js';

// The header VIDEO of a WhatsApp template (campaign composer + journey node):
// upload a clip from the device (MP4/3GP, WhatsApp's 16 MB cap) — it lands on
// Pulse at a public address the send pipeline fetches once per campaign — or
// paste a hosted address. The value is always a URL. server/waMedia.js.
const MAX = 16 * 1024 * 1024;
// WhatsApp (Meta) takes MP4 (H.264 + AAC) or 3GP for a video header — nothing
// else, however ordinary the file. A phone clip is very often .mov (QuickTime),
// which looks like a video everywhere except here, so the picker accepts any
// video and says what is wrong by name rather than greying the file out.
const KIND = { 'video/mp4': 1, 'video/3gpp': 1 };
const extOf = (file) => (KIND[file.type] ? '' : ((String(file.name).match(/\.([a-z0-9]+)$/i) || ['', 'file'])[1] || 'file').toLowerCase());
const mb = (n) => `${(n / 1048576).toFixed(1)} MB`;
/// Everything wrong with this file, in one message — a clip can be both.
function whyNot(file) {
  const bad = [];
  const ext = extOf(file);
  if (ext) bad.push(`WhatsApp only takes MP4 (H.264 + AAC) or 3GP — this is a .${ext}${/quicktime|mov/i.test(`${file.type} ${ext}`) ? ' (QuickTime)' : ''}. Export or convert it to MP4.`);
  if (file.size > MAX) bad.push(`WhatsApp caps a header video at 16 MB — this one is ${mb(file.size)}. Trim it or export at a smaller size.`);
  return bad.join(' ');
}
const input = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1.5px solid var(--hairline)', borderRadius: 8, fontSize: 13, outline: 'none', background: 'var(--card)', color: 'var(--text)' };
const mini = { padding: '7px 12px', background: 'rgba(128,128,128,0.10)', color: 'var(--text)', border: '1px solid var(--hairline)', borderRadius: 980, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 36 };
const hint = { fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.4 };

export default function VideoField({ value, onChange, entityId, compact = false }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const onFile = (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setErr('');
    const no = whyNot(file);
    if (no) { setErr(no); return; }
    const mime = file.type || (/\.3gp$/i.test(file.name) ? 'video/3gpp' : 'video/mp4');
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        const data = String(reader.result || '').split(',')[1] || '';
        const r = await api.uploadWaMedia(entityId, { name: file.name, mime, data });
        onChange(r.media.url);
      } catch (x) { setErr(x.message); } finally { setBusy(false); }
    };
    reader.onerror = () => setErr('Could not read that file');
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" style={mini} disabled={busy} onClick={() => ref.current?.click()}>{busy ? 'Uploading…' : (value ? 'Replace clip' : 'Upload a clip')}</button>
        {value && <button type="button" style={{ ...mini, color: 'var(--error,#ef4444)' }} onClick={() => onChange('')}>Remove</button>}
        <input ref={ref} type="file" accept="video/*" style={{ display: 'none' }} onChange={onFile} />
      </div>
      <input style={{ ...input, marginTop: 6 }} value={value || ''} onChange={(e) => onChange(e.target.value.trim())} placeholder="or paste a hosted MP4 (H.264 + AAC) or 3GP address, under 16 MB" />
      {!compact && <div style={hint}>Upload from your phone or laptop, or paste a clip's address (it must open directly as a video file, not a page). Landscape works best; keep it short — a few seconds to a minute. Sent to WhatsApp once per campaign.</div>}
      {err && <div style={{ ...hint, color: 'var(--error,#ef4444)' }}>{err}</div>}
      {value && <video src={value} controls preload="metadata" style={{ display: 'block', width: '100%', maxWidth: 340, borderRadius: 10, marginTop: 8, border: '1px solid var(--hairline)' }} />}
    </div>
  );
}
